import React, { useEffect } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { useEvents } from '@/hooks/use-events';
import { useMessage } from '@/hooks/use-message';
import { usePenalty } from '@/hooks/use-penalty';
import { useNotifications } from '@/hooks/use-notifications';
import { useSupabase } from '@/contexts/supabase/SupabaseClientProvider';
import { formatEventTime } from '@/utils/dateUtils';

interface NotificationDataProviderProps {
  children: React.ReactNode;
}

export function NotificationDataProvider({ children }: NotificationDataProviderProps) {
  const { user } = useAuth();
  const { events } = useEvents();
  const { conversations, messages } = useMessage();
  const { penalties } = usePenalty();
  const { addNotification } = useNotifications();
  const supabase = useSupabase();
  const isUuid = (v: string) => /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i.test(v);

  // Generate notifications from various sources
  useEffect(() => {
    if (!user || !isUuid(user.id)) return;

    const processNotifications = async () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('NotificationDataProvider: Generating notifications...');
      }

      // Fetch existing notification IDs from database to prevent duplicates
      const { data: existingNotifs } = await supabase
        .from('notifications')
        .select('metadata')
        .eq('user_id', user.id)
        .not('metadata->>notificationId', 'is', null);

      const existingNotificationIds = new Set(
        (existingNotifs || [])
          .map(n => {
            const meta = n.metadata as { notificationId?: string } | null;
            return meta?.notificationId;
          })
          .filter(Boolean) as string[]
      );
      
      // Event-based notifications
      events.forEach(event => {
        const isParticipant = event.participants.includes(user.id);
        const isOnWaitingList = event.waitingList?.includes(user.id);
        
        if (isParticipant) {
          const eventDate = typeof event.date === 'string' ? new Date(event.date) : event.date;
          const eventTime = new Date(`${eventDate.toDateString()} ${event.time}`);
          const now = new Date();
          const timeDiff = eventTime.getTime() - now.getTime();
          const minutesUntilEvent = timeDiff / (1000 * 60);

          // 3-hour reminder: Only trigger between 2h50m and 3h10m before event
          const minutesUntil3h = 3 * 60;
          const reminder3hId = `event-reminder-3h-${event.id}`;
          if (minutesUntilEvent <= minutesUntil3h + 10 && minutesUntilEvent >= minutesUntil3h - 10 && !existingNotificationIds.has(reminder3hId)) {
            existingNotificationIds.add(reminder3hId);
            addNotification({
              type: 'event',
              title: 'Event Starting in 3 Hours',
              description: `"${event.title}" starts in 3 hours at ${formatEventTime(event.time)} - ${event.location}`,
              userId: user.id,
              actionUrl: `/event/${event.id}`,
              priority: 'medium',
              metadata: { eventId: event.id, notificationId: reminder3hId }
            });
          }

          // 24-hour reminder for tournaments: Only trigger between 23h50m and 24h10m before event
          if (event.type === 'tournament') {
            const minutesUntil24h = 24 * 60;
            const reminder24hId = `event-reminder-24h-${event.id}`;
            if (minutesUntilEvent <= minutesUntil24h + 10 && minutesUntilEvent >= minutesUntil24h - 10 && !existingNotificationIds.has(reminder24hId)) {
              existingNotificationIds.add(reminder24hId);
              addNotification({
                type: 'tournament',
                title: 'Tournament Tomorrow',
                description: `"${event.title}" tournament starts tomorrow at ${formatEventTime(event.time)} - ${event.location}`,
                userId: user.id,
                actionUrl: `/event/${event.id}`,
                priority: 'medium',
                metadata: { eventId: event.id, notificationId: reminder24hId }
              });
            }
          }

        }

        if (isOnWaitingList) {
          const waitlistId = `waitlist-${event.id}`;
          if (!existingNotificationIds.has(waitlistId)) {
            existingNotificationIds.add(waitlistId);
            addNotification({
              type: 'event',
              title: 'Waiting List Position',
              description: `You're on the waiting list for "${event.title}"`,
              userId: user.id,
              actionUrl: `/event/${event.id}`,
              priority: 'low',
              metadata: { eventId: event.id, notificationId: waitlistId }
            });
          }
        }
      });

      // Message notifications
      conversations.forEach(conversation => {
        const conversationMessages = messages[conversation.id] || [];
        const unreadMessages = conversationMessages.filter(msg => 
          !msg.isRead && msg.senderId !== user.id
        );

        if (unreadMessages.length > 0) {
          const messageId = `message-${conversation.id}`;
          if (!existingNotificationIds.has(messageId)) {
            existingNotificationIds.add(messageId);
            const latestMessage = unreadMessages[unreadMessages.length - 1];
            addNotification({
              type: 'message',
              title: `New ${unreadMessages.length > 1 ? 'Messages' : 'Message'}`,
              description: unreadMessages.length > 1 
                ? `${unreadMessages.length} new messages in ${conversation.name}`
                : `New message from ${latestMessage.senderName}: ${latestMessage.content.substring(0, 50)}...`,
              userId: user.id,
              actionUrl: '/messages',
              priority: latestMessage.isAdmin ? 'high' : 'medium',
              metadata: { conversationId: conversation.id, messageCount: unreadMessages.length, notificationId: messageId }
            });
          }
        }
      });

      // Penalty notifications
      penalties.forEach(memberPenalty => {
        if (memberPenalty.memberId === user.id && memberPenalty.records.length > 0) {
          const latestPenalty = memberPenalty.records[memberPenalty.records.length - 1];
          const penaltyTimestamp = new Date(latestPenalty.timestamp);
          const penaltyAge = Date.now() - penaltyTimestamp.getTime();
          const daysSincePenalty = penaltyAge / (1000 * 60 * 60 * 24);

          if (daysSincePenalty < 1) {
            const penaltyId = `penalty-${latestPenalty.id}`;
            if (!existingNotificationIds.has(penaltyId)) {
              existingNotificationIds.add(penaltyId);
              addNotification({
                type: 'penalty',
                title: 'Penalty Assigned',
                description: `You received a penalty for ${latestPenalty.type.replace('_', ' ')}. Current dots: ${memberPenalty.totalDots}`,
                userId: user.id,
                actionUrl: '/profile',
                priority: 'high',
                metadata: { penaltyId: latestPenalty.id, totalDots: memberPenalty.totalDots, notificationId: penaltyId }
              });
            }
          }
        }
      });
    };

    processNotifications();
  }, [user, events, conversations, messages, penalties, addNotification, supabase]);

  // Daily/latest tournament results notification (with state to avoid repeats)
  useEffect(() => {
    if (!user || !isUuid(user.id)) return;

    let cancelled = false;
    const run = async () => {
      try {
        const { data: latest, error } = await supabase
          .from('tournaments')
          .select('id, name, completed_at, is_completed')
          .eq('is_completed', true)
          .order('completed_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (cancelled) return;
        if (error || !latest) return; // nothing to notify about

        // Skip if no matches recorded for this tournament (avoid empty page)
        const { count } = await supabase
          .from('matches')
          .select('*', { count: 'exact', head: true })
          .eq('tournament_id', latest.id);
        if ((count || 0) === 0) return;

        // Read user notification state
        const { data: stateRow } = await supabase
          .from('user_notification_state')
          .select('last_seen_tournament_id, last_notif_date')
          .eq('user_id', user.id)
          .maybeSingle();
        const lastSeenId = stateRow?.last_seen_tournament_id || null;
        const lastDaily = stateRow?.last_notif_date ? new Date(stateRow.last_notif_date).toDateString() : null;
        const today = new Date().toDateString();

        const isNewTournament = lastSeenId !== latest.id;
        const sentToday = lastDaily === today;

        if (isNewTournament || !sentToday) {
          addNotification({
            type: 'tournament',
            title: 'Latest Tournament Results',
            description: 'Results are available for the latest tournament',
            userId: user.id,
            actionUrl: `/tournaments/${latest.id}`,
            priority: 'low',
            metadata: { tournamentId: latest.id, notificationId: `tournament-${latest.id}` }
          });
          await supabase
            .from('user_notification_state')
            .upsert({
              user_id: user.id,
              last_seen_tournament_id: latest.id,
              last_notif_date: new Date().toISOString(),
            }, { onConflict: 'user_id' });
        }
      } catch (e) {
        // ignore errors; this is a best-effort helper
        console.warn('Tournament notification check failed:', e);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [user, addNotification]);

  // Listen for freshly recorded tournaments and notify immediately
  useEffect(() => {
    if (!user || !isUuid(user.id)) return;
    const handler = async (e: Event) => {
      const detail = (e as CustomEvent).detail as { id?: string; name?: string } | undefined;
      if (!detail?.id) return;

      const { data: stateRow } = await supabase
        .from('user_notification_state')
        .select('last_seen_tournament_id, last_notif_date')
        .eq('user_id', user.id)
        .maybeSingle();
      const lastSeenId = stateRow?.last_seen_tournament_id || null;
      const lastDaily = stateRow?.last_notif_date ? new Date(stateRow.last_notif_date).toDateString() : null;
      const today = new Date().toDateString();

      const isNewTournament = lastSeenId !== detail.id;
      const sentToday = lastDaily === today;

      if (isNewTournament || !sentToday) {
        addNotification({
          type: 'tournament',
          title: 'Tournament Results',
          description: detail.name
            ? `Results for ${detail.name} are available`
            : 'Latest tournament results are available',
          userId: user.id,
          actionUrl: `/tournaments/${detail.id}`,
          priority: 'low',
          metadata: { tournamentId: detail.id, notificationId: `tournament-${detail.id}` }
        });
        await supabase
          .from('user_notification_state')
          .upsert({
            user_id: user.id,
            last_seen_tournament_id: detail.id,
            last_notif_date: new Date().toISOString(),
          }, { onConflict: 'user_id' });
      }
    };
    window.addEventListener('tournament-recorded', handler as EventListener);
    return () => window.removeEventListener('tournament-recorded', handler as EventListener);
  }, [user, addNotification]);

  return <>{children}</>;
}
